import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';

import {
  useFitment,
  useMyAccount,
  useMyStore,
  useOptimisticData,
  usePrevious,
  useProductDetail
} from '../../../../shared/hooks/hooks';

import {
  getIsNoLongerAvailable,
  getNormalizedProductType,
  getPrioritizedArticleStatusProduct
} from '../../../../shared/utils/product/product';
import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

interface PageParams {
  packageCode?: string;
  productCode?: string;
  productSlug?: string;
  productType?: string;
  rearProductCode?: string;
}

interface LocationState {
  initialProductCodes?: string[];
}

export interface QueryFiltersState {
  name?: string;
  value?: string;
}

function usePDPProduct(searchTypes, treadwellPreferences, isTreadwellFlow) {
  const [isLoading, setIsLoading] = useState(true);
  const [isCanonicalWithoutFilterOptions, setIsCanonicalWithoutFilterOptions] =
    useState(false);
  const [error, setError] = useState<string>();
  const [isNoLongerAvailable, setIsNoLongerAvailable] = useState(false);
  const [queryFilters, setQueryFilters] = useState<QueryFiltersState[] | null>(
    null
  );
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(true);
  const [shouldFetchAllCanonicalData, setShouldFetchAllCanonicalData] =
    useState(false);
  const urlParams = useParams<PageParams>();
  const optimisticData = useOptimisticData();
  const { myStore, nearbyStorePreferences } = useMyStore();
  const { isFitmentLoading, selectedFitment } = useFitment();
  const prevSelectedFitment = usePrevious(selectedFitment, true);
  const { pathname, search, state } = useLocation<LocationState>();
  const { fitmentPosition } = queryString.parse(search) || {};
  const prevPathname = usePrevious(pathname, true);
  const isInitializedRef = useRef(false);

  const { isFleetAccount } = useMyAccount();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const {
    packageCode,
    productCode,
    productSlug,
    productType,
    rearProductCode
  } = urlParams;
  const isStaggered = Boolean(rearProductCode);
  const isSetSearch = Boolean(pathname?.includes('set'));
  const isPrebuiltPackage = Boolean(packageCode);
  const isCanonicalSearch = !productCode && !packageCode;

  const [product, setProduct] = useState(() => {
    let defaultValue;

    if (isPrebuiltPackage) {
      defaultValue = optimisticData.read('prebuiltPackage') || null;
    } else {
      defaultValue = optimisticData.read('product') || null;
    }

    return defaultValue;
  });

  const [weatherDetails, setWeatherDetails] = useState(null);

  const storeCode = window.isPreRendering ? '1344' : myStore?.code;
  const initialProductCodes = state?.initialProductCodes;
  const nearbyStoreCodes = useMemo(
    () => myStore?.nearByStores?.map(store => store?.code),
    [myStore?.nearByStores]
  );
  const { getProductDetail } = useProductDetail({
    fitmentPosition,
    myStore,
    nearByStoreCodes: isArrayWithLength(nearbyStorePreferences)
      ? nearbyStorePreferences
      : nearbyStoreCodes
  });

  const shouldFetchProduct =
    storeCode && !isFitmentLoading && (isRefreshingData || shouldFetchData);

  const getProduct = useCallback(async () => {
    if (!myStore?.code) {
      return;
    }

    let queryType;

    try {
      setIsLoading(true);
      setShouldFetchData(false);

      const hasFilters = Boolean(queryFilters);
      const {
        errors,
        product,
        queryType: queryTypeResult,
        recommendations
      } = await getProductDetail({
        canonicalFilters: queryFilters,
        canonicalProductCodes: initialProductCodes || [],
        canonicalProductType: getNormalizedProductType(productType),
        fitment: selectedFitment,
        isFleetTreadwell,
        isTreadwellFlow,
        packageCode,
        productCode,
        productSlug,
        rearProductCode,
        searchTypes,
        shouldFetchAllCanonicalData,
        store: myStore,
        treadwellPreferences
      });

      queryType = queryTypeResult;

      const articleStatusProduct = isPrebuiltPackage
        ? getPrioritizedArticleStatusProduct(
            product?.product1,
            product?.product2
          )
        : product;
      const isNoLongerAvailable = getIsNoLongerAvailable(articleStatusProduct);
      const newProduct = product || null;
      const newWeatherDetails = recommendations?.weatherDetails || null;

      if (
        Array.isArray(errors) ||
        (isCanonicalSearch && shouldFetchAllCanonicalData && !newProduct) ||
        (!isCanonicalSearch && !newProduct)
      ) {
        throw errors?.[0] || 'no product';
      }

      setIsCanonicalWithoutFilterOptions(
        Boolean(
          isCanonicalSearch &&
            newProduct &&
            newProduct.productType !== 'aggregate' &&
            !hasFilters
        )
      );

      setProduct(newProduct);
      setWeatherDetails(newWeatherDetails);
      setIsNoLongerAvailable(isNoLongerAvailable || false);
    } catch (err) {
      setError(
        `No ${queryType} product found | ${(err as Error)?.message || err}`
      );
    } finally {
      setIsLoading(false);
      setIsRefreshingData(false);
    }
  }, [
    myStore,
    queryFilters,
    getProductDetail,
    isFleetTreadwell,
    initialProductCodes,
    productType,
    selectedFitment,
    isTreadwellFlow,
    packageCode,
    productCode,
    productSlug,
    rearProductCode,
    searchTypes,
    shouldFetchAllCanonicalData,
    treadwellPreferences,
    isPrebuiltPackage,
    isCanonicalSearch
  ]);

  function refetchData() {
    setShouldFetchData(true);
  }

  useEffect(() => {
    function resetData() {
      setQueryFilters(null);
      setProduct(null);
      setIsLoading(true);
      setIsRefreshingData(true);
      setShouldFetchAllCanonicalData(false);
    }

    if (
      isInitializedRef.current &&
      (prevSelectedFitment?.vehicleAssemblyId !==
        selectedFitment?.vehicleAssemblyId ||
        prevPathname !== pathname)
    ) {
      resetData();
    }
  }, [selectedFitment, prevSelectedFitment, pathname, prevPathname]);

  useEffect(() => {
    if (queryFilters || pathname) {
      setShouldFetchData(true);
    }
  }, [queryFilters, pathname]);

  useEffect(() => {
    // receiving null for canonical product when a vehicle is selected means
    // none of the products in the aggregate fit the vehicle so we instead
    // refetch all of the canonical data regardless of fit
    if (isCanonicalSearch && !isLoading && !product) {
      setQueryFilters(null);
      setShouldFetchAllCanonicalData(true);
      setShouldFetchData(true);
    }
  }, [isCanonicalSearch, product, isLoading]);

  useEffect(() => {
    if (shouldFetchProduct) {
      getProduct();
    }
  }, [shouldFetchProduct, getProduct]);

  return {
    error,
    isCanonicalSearch,
    isCanonicalWithoutFilterOptions,
    isLoading,
    isNoLongerAvailable,
    isPrebuiltPackage,
    isRefreshingData,
    isSetSearch,
    isStaggered,
    product,
    queryFilters,
    refetchData,
    setQueryFilters,
    weatherDetails
  };
}

export default usePDPProduct;
